import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useState, useEffect } from 'react';

interface SearchResult {
  type: 'deal' | 'matter' | 'property' | 'document' | 'person';
  id: string;
  title: string;
  subtitle: string;
  link: string;
}

export function useGlobalSearch(query: string) {
  const { user, profile } = useAuth();
  const [debouncedQuery, setDebouncedQuery] = useState(query);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 300);
    return () => clearTimeout(timer);
  }, [query]);

  return useQuery<SearchResult[]>({
    queryKey: ['global-search', debouncedQuery, user?.id],
    enabled: !!user && debouncedQuery.trim().length >= 2,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('search_all', {
        query: debouncedQuery.trim(),
        user_role: profile?.role || 'client',
        p_user_id: user!.id,
      });
      if (error) throw error;
      return (data as unknown as SearchResult[]) || [];
    },
    staleTime: 30_000,
  });
}

export function groupSearchResults(results: SearchResult[] | undefined) {
  if (!results) return {};
  return results.reduce<Record<string, SearchResult[]>>((acc, r) => {
    (acc[r.type] ||= []).push(r);
    return acc;
  }, {});
}
